import React, { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { useProjectStore, useSuperHubStore } from '@core/store';
import groupBy from '@core/utils/objectGroupBy';

import type { LangPickerProps } from '@ui/i18n/LangPicker';
import LangPicker from '@ui/i18n/LangPicker';

export default function LanguagePicker({ label }: Pick<LangPickerProps, 'label'>) {
  const [subdomain, enabledLanguages] = useProjectStore(s => [s.data.subdomain, s.data.i18n.languages]);
  const [currentLanguage, currentVersion, versions] = useSuperHubStore(s => [
    s.language,
    s.getCurrentVersion(),
    s.versions.data.data,
  ]);
  const { pathname, search, hash } = useLocation();

  const buildLanguageUrl = useCallback(
    (lang: string) => {
      const cleanVersion = currentVersion.name.split('-')[0]; // remove existing language suffix if present
      const langSuffix = lang === 'en' ? '' : `-${lang}`; // only add suffix if not default language
      return `/${subdomain}/v${cleanVersion}${langSuffix}${pathname}${search}${hash}`;
    },
    [currentVersion.name, pathname, search, hash, subdomain],
  );

  const languages = useMemo(() => {
    // hide languages that don't have any associated versions
    const contentfulLangs = groupBy(versions, ({ i18n }) => i18n.lang);
    return enabledLanguages.filter(lang => !!contentfulLangs[lang]);
  }, [versions, enabledLanguages]);

  return (
    <LangPicker
      buttonClassName="rm-Header-top-link"
      circular={false}
      enabledLanguages={languages}
      ghost
      initialLanguage={currentLanguage}
      kind="secondary"
      label={label}
      onChange={(lang: string) => {
        // Issue redirect to the same page, but in the selected language
        window.location.assign(buildLanguageUrl(lang));
      }}
    />
  );
}
